import React, { useState, useEffect } from 'react'
import { Input } from '../../ui/input'
import { Button } from '../../ui/button'
import { supabase } from '../../../lib/supabase'
import { toast } from 'sonner'
import { debug } from '../../../lib/debug'
import { SURVEY_CONFIG } from '../../../lib/constants'

interface CreateSurveyFormProps {
  onSuccess: () => void
  onCancel: () => void
}

export function CreateSurveyForm({ onSuccess, onCancel }: CreateSurveyFormProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState<{
    organization_name: string;
    expires_days: number;
  }>({
    organization_name: '',
    expires_days: 7
  })
  const [managerId, setManagerId] = useState<string | null>(null)

  // Get the manager ID for the current user
  useEffect(() => {
    const getManagerId = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data: manager } = await supabase
        .from('survey_managers')
        .select('id')
        .eq('user_id', user.id)
        .single()

      if (manager) {
        setManagerId(manager.id)
      }
    }

    getManagerId()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    debug('SURVEY_CREATOR', 'Creating new survey:', formData)

    try {
      const user = await supabase.auth.getUser()
      if (!user.data.user) throw new Error('Not authenticated')
      if (!managerId) throw new Error('No manager ID found')

      const expiryDate = new Date()
      expiryDate.setDate(expiryDate.getDate() + formData.expires_days)

      // First, generate a survey code
      const { data: codeData, error: codeError } = await supabase
        .rpc('generate_survey_code')

      if (codeError) {
        debug('SURVEY_CREATOR', 'Error generating survey code:', codeError)
        throw codeError
      }

      debug('SURVEY_CREATOR', 'Generated survey code:', codeData)

      // Then create the survey with the generated code
      const { data, error } = await supabase
        .from('survey_codes')
        .insert({
          code: codeData,
          organization_name: formData.organization_name,
          expires_at: expiryDate.toISOString(),
          organizer_email: user.data.user.email,
          manager_id: managerId,
          status: 'active'
        })
        .select()
        .single()

      if (error) {
        debug('SURVEY_CREATOR', 'Error creating survey:', error)
        throw error
      }

      debug('SURVEY_CREATOR', 'Survey created successfully:', data)
      toast.success('Survey created successfully')
      onSuccess()
    } catch (error) {
      console.error('Error creating survey:', error)
      toast.error('Failed to create survey')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="organization_name" className="text-sm font-medium">
          Organisation Name
        </label>
        <Input
          id="organization_name"
          value={formData.organization_name}
          onChange={(e) => setFormData(prev => ({ ...prev, organization_name: e.target.value }))}
          required
          disabled={isLoading}
          placeholder="Enter organisation name"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="expires_days" className="text-sm font-medium">
          Expires After (days)
        </label>
        <Input
          id="expires_days"
          type="number"
          min={1}
          max={365}
          value={formData.expires_days}
          onChange={(e) => setFormData(prev => ({ ...prev, expires_days: parseInt(e.target.value) }))}
          required
          disabled={isLoading}
        />
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="ghost"
          className="flex-1"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1"
          disabled={isLoading || !managerId}
        >
          {isLoading ? 'Creating Survey...' : 'Create Survey'}
        </Button>
      </div>
    </form>
  )
}